/**
 * Runtime-agnostic parsers for Spine atlas and skeleton JSON.
 * Used by Poser (fallback map) and can be used by pixi-framework or other consumers
 * that need page/region or region->skin mapping without loading textures.
 */

export interface SpineAtlasPageRegions {
  /**
   * Matches the texture name: e.g. "player_bot_9.png"
   */
  pageName: string;
  /**
   * e.g. "3pig_body/body", "3pig_body/lights"
   */
  regionNames: string[];
}

/**
 * Parse Spine .atlas text into page names and their region names.
 * Matches the libgdx/Spine atlas format: page name line, then key: value props, then region name lines.
 */
export function parseSpineAtlasToPageRegions(atlasText: string): SpineAtlasPageRegions[] {
  const pages: SpineAtlasPageRegions[] = [];
  const lines = atlasText.split(/\r?\n/);
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    i++;
    if (!trimmed) { continue; }

    if (!trimmed.includes(':')) {
      const pageName = trimmed;
      const regionNames: string[] = [];
      while (i < lines.length) {
        const next = lines[i];
        const nextTrim = next.trim();
        i++;
        if (!nextTrim) { break; }

        if (nextTrim.includes(':')) { continue; }
        regionNames.push(nextTrim);
      }
      pages.push({ pageName, regionNames });
    }
  }
  return pages;
}

/** Skeleton JSON skin entry (minimal shape we need). */
export interface SpineSkinJson {
  name?: string;
  attachments?: Record<string, Record<string, { path?: string; name?: string }>>;
}

/**
 * Build map: normalized region path -> partSkin name, from non-default skins only.
 * e.g. {"3pig_body/body" => "pig_body"}, {"3pig_body/lights" => "pig_body"}.
 * Skeleton JSON shape: skins array at root or under .skeleton; each skin has { name, attachments: { slotKey: { attKey: { path or name } } } }.
 */
export function buildRegionPathToPartSkinFromSkeleton(skeletonJson: { skins?: SpineSkinJson[]; skeleton?: { skins?: SpineSkinJson[] } }): Map<string, string> {
  const map = new Map<string, string>();
  const skins = skeletonJson.skins ?? skeletonJson.skeleton?.skins ?? [];
  const defaultSkinName = 'default';
  for (const skin of skins) {
    const skinName = skin.name;
    if (!skinName || skinName === defaultSkinName) { continue; }

    if (!skin.attachments) { continue; }
    for (const attachments of Object.values(skin.attachments)) {
      if (!attachments || typeof attachments !== 'object') { continue; }
      for (const att of Object.values(attachments)) {
        if (!att || typeof att !== 'object') { continue; }
        const path = att.path ?? att.name;
        if (typeof path !== 'string') { continue; }
        map.set(path, skinName);
      }
    }
  }
  return map;
}
